import React, { useState,useRef, useEffect, useContext, useMemo } from "react";
import {
  Row,
  Container,
  Card,
  CardBody,
  Col,
  Alert,
  Form,
  FormGroup,
  Input,
  Label,
} from "reactstrap";
import * as Yup from "yup";
import { useFormik } from "formik";
import Select from "react-select";
// Pages
import Breadcrumbs from "../../../components/common/Breadcrumb";
import {
  handleAddEditAppSettingsApi,
  fetchAppSettingsDetailsApi,
  fetchCountrySpecificTaxDetailsApi
} from "../../../api/appSettingsApi";
import {
  fetchActiveTaxDetailsApi
} from "../../../api/taxationsApi";
import { useAppSettings } from '../../../contexts/AppSettingsContext';

import { AuthContext } from "../../../contexts/AuthContext";
import countries from "../../../utils/currency/currency.json";
import { taxTypes } from "../../../utils/constants";

function Settings() {
  const { appSettings, setAppSettings } = useAppSettings();
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [taxList, setTaxList] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState({})
  const { authToken } = useContext(AuthContext);
  const [countrySpecificTaxDetails, setCountrySpecificTaxDetails] = useState([])
  const appName = appSettings?.title ||process.env.REACT_APP_APP_NAME;
  const scrollableDivRef = useRef(null);
  // Set token
  useEffect(() => {
    if (authToken) {
      fetchActiveTaxDetails();
      setSuccessMessage("");
      setErrorMessage("");
    }
  }, [authToken]);

  const selectedCountryDetails = useMemo(() => {
    return countries.find((country) => country.code === appSettings?.country?.currency_code);
  }, [countries, appSettings]);
  
  
  useEffect(() => {
    if (appSettings && Object.keys(appSettings).length !== 0 && selectedCountryDetails) {
      const countryOption = {
        label: (
          <div style={{ display: "flex", alignItems: "center" }}>
            <img
              src={selectedCountryDetails.flag}
              alt={`${selectedCountryDetails.country} flag`}
              style={{ width: 20, height: 20, marginRight: 10 }}
            />
            <span>{`${selectedCountryDetails.country} (${selectedCountryDetails.countryCode})`}</span>
          </div>
        ),
        value: selectedCountryDetails.country,
        currency: selectedCountryDetails.code
      };
      setSelectedCountry(countryOption);
    }
  }, [appSettings, selectedCountryDetails]);
  

  // Function to scroll top
  const scrollToTop = () => {
    if (scrollableDivRef.current) {
      scrollableDivRef.current.scrollTo({
        top: 0,
        behavior: "smooth", // Smooth scrolling
      });
    }
  };

  const memoizedCountryData = useMemo(() => {
    return countries.map((item) => ({
      label: (
        <div style={{ display: "flex", alignItems: "center" }}>
          <img
            src={item.flag}
            alt={`${item.country} flag`}
            style={{ width: 20, height: 20, marginRight: 10 }}
          />
          <span>{`${item.country} (${item.countryCode})`}</span>
        </div>
      ),
      value: item?.country,
      currency : item.code
    }));
  }, [countries]);


    // Function for handle selected country
    const handleCountrySelectChange = async(selected) => {
      try{  
        if(selected.currency){
          const response = await fetchCountrySpecificTaxDetailsApi(selected.currency)
          if (response?.code == 200) {
            setCountrySpecificTaxDetails(response?.data)
          } else {
            setErrorMessage(response?.message || "");
          }
        }
      }catch(error){

      }
      setSelectedCountry(selected);
    };
  

  // #Handle customer form
  const settingsForm = useFormik({
    enableReinitialize: true,
    initialValues: {
      title: appSettings.title || "",
      contact_no: appSettings.contact_no || "",
      email: appSettings.email || "",
      logo: appSettings.logo || null,
      login_bg_image: appSettings.login_bg_image || null,
      favicon: appSettings.favicon || null,
      address: appSettings.address ||"",
      arabic_tag_line: appSettings.arabic_tag_line  || "",
      tag_line: appSettings.tag_line || "",
      country: {},
      tax: countrySpecificTaxDetails?.tax_id || appSettings.tax_id || "",
      tax_type : countrySpecificTaxDetails?.tax_type || appSettings.tax_type || "",
      tax_value : countrySpecificTaxDetails?.tax_value || appSettings.tax_value || "",
      tax_no: appSettings.tax_no || "",
      quote_prefix: appSettings.quote_prefix || "",
      license_number : appSettings.license_number ||"",
      standard_markup : appSettings.standard_markup ||"",
      quote_validity_period : appSettings.quote_validity_period ||"",
      quote_delivery_terms : appSettings.quote_delivery_terms ||"",
      quote_payment_structure : appSettings.quote_payment_structure ||"",
    },
    validationSchema: Yup.object({}),
    onSubmit: (values) => {
      // Trim all string values
      const trimmedValues = Object.keys(values).reduce((acc, key) => {
        acc[key] =
          typeof values[key] == "string" ? values[key].trim() : values[key];
        return acc;
      }, {});

      if(Object.keys(selectedCountry)?.length == 0){
        setErrorMessage("Select country");
        scrollToTop();
        return
      }
      trimmedValues.country  = {name : selectedCountry.name, currency_code : selectedCountry.currency}
      trimmedValues.isEdit = false;
      if (Object.keys(appSettings)?.length !== 0) {
        const appSettingsId = appSettings?.id;
        trimmedValues.isEdit = true;
        trimmedValues.id = appSettingsId;
      }
      console.log("submitted values ->", trimmedValues);

      handleAddEditApplicationSettings(trimmedValues);
    },
  });

  // Function for fetch tax active tax details 
  async function fetchActiveTaxDetails() {
    try {
      if (!authToken) {
        throw new Error("Token not found.");
      }

      let response = await fetchActiveTaxDetailsApi(authToken);
      if (response?.code == 200) {
        setTaxList(response.data)
      } else {
        setErrorMessage(response?.message || "");
      }
    } catch (error) {
      setErrorMessage("Something went wrong");
    }
  }

  // Function handle add & edit application settings
  async function handleAddEditApplicationSettings(values) {
    try {
      if (!authToken) {
        throw new Error("Token not found.");
      }

      let response = await handleAddEditAppSettingsApi(values, authToken);
      if (response?.code == 200) {
        scrollToTop();
        setErrorMessage("");
        setSuccessMessage(response.message);
        fetchApplicationSettingsDetails();
      } else {
        setSuccessMessage("");
        scrollToTop();
        setErrorMessage(response?.message || "");
      }
    } catch (error) {
      console.log(error.message);
      scrollToTop();
      setSuccessMessage("");
      setErrorMessage("Something went wrong");
    }
  }

  // Function for fetch application settings details
  const fetchApplicationSettingsDetails = async () => {
    try {

      let response = await fetchAppSettingsDetailsApi();
      if (response?.code == 200) {
        const data = response?.data;
        setAppSettings(data || []);
      }
    } catch (err) {
      console.log("err", err.message);
    }
  };

  return (
    <>
      <div className="page-content">
        <Container fluid={true}>
          <Breadcrumbs title={appName} breadcrumbItem="Settings" />
          <Row className="d-flex justify-content-center">
            <Col md={8}>
              <Card>
                <CardBody>
                  {errorMessage && (
                    <Alert  color="danger">
                      <div>{errorMessage}</div>
                    </Alert>
                  )}
                  <div      ref={scrollableDivRef}></div>
                  {successMessage && (
                    <Alert color="success">
                      <div>{successMessage}</div>
                    </Alert>
                  )}

                  <Form onSubmit={settingsForm.handleSubmit}>
                    <Col md={12}>
                      <FormGroup>
                        <Label htmlFor="title">Company Title:</Label>
                        <Input
                          type="text"
                          value={settingsForm.values.title}
                          required
                          onChange={settingsForm.handleChange}
                          id="title"
                          name="title"
                        />
                      </FormGroup>
                    </Col>
                    <Row>
                      <Col md={6}>
                        <FormGroup>
                          <Label htmlFor="app_contact_no">
                            Contact Number:
                          </Label>
                          <Input
                            type="text"
                            value={settingsForm.values.contact_no}
                            required
                            onChange={settingsForm.handleChange}
                            id="app_contact_no"
                            name="contact_no"
                          />
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label htmlFor="app_email">Email:</Label>
                          <Input
                            type="email"
                            value={settingsForm.values.email}
                            required
                            onChange={settingsForm.handleChange}
                            id="app_email"
                            name="email"
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    {/* Logo */}
                    <Col md={10}>
                      <FormGroup>
                        <Label htmlFor="app_logo">Logo:</Label>
                        {settingsForm.values?.logo && (
                          <img
                            src={
                              typeof settingsForm.values?.logo == "string"
                                ? settingsForm.values?.logo
                                : URL.createObjectURL(settingsForm.values?.logo)
                            }
                            alt="App Logo Preview"
                            className="productImagePreview"
                          />
                        )}
                        <Input
                          type="file"
                          required={
                            typeof settingsForm.values?.logo !== "string"
                          }
                          onChange={(event) => {
                            const file = event.currentTarget.files[0];
                            if (
                              file &&
                              (file.type === "image/jpeg" ||
                                file.type === "image/png")
                            ) {
                              settingsForm.setFieldValue("logo", file);
                            } else {
                              // If invalid, clear the input and optionally show an error message
                              event.target.value = null;
                              if (
                                !(settingsForm.values?.image instanceof File)
                              ) {
                                settingsForm.setFieldError(
                                  "logo",
                                  "Only JPG or PNG files are allowed."
                                );
                              }
                            }
                          }}
                          id="app_logo"
                          name="logo"
                        />
                        {settingsForm.errors.logo && (
                          <div className="invalidError d-flex justify-content-start">
                            {settingsForm.errors.logo}
                          </div>
                        )}
                      </FormGroup>
                    </Col>
                    <Col md={10}>
                      <FormGroup>
                        <Label htmlFor="app_login_bg_image ">
                          Login Background Image:
                        </Label>
                        {settingsForm.values?.login_bg_image && (
                          <img
                            src={
                              typeof settingsForm.values?.login_bg_image ==
                              "string"
                                ? settingsForm.values?.login_bg_image
                                : URL.createObjectURL(
                                    settingsForm.values?.login_bg_image
                                  )
                            }
                            alt="App login background Preview"
                            className="productImagePreview"
                          />
                        )}
                        <Input
                          type="file"
                          onChange={(event) => {
                            const file = event.currentTarget.files[0];
                            if (
                              file &&
                              (file.type === "image/jpeg" ||
                                file.type === "image/png")
                            ) {
                              settingsForm.setFieldValue(
                                "login_bg_image",
                                file
                              );
                            } else {
                              // If invalid, clear the input and optionally show an error message
                              event.target.value = null;
                              if (
                                !(
                                  settingsForm.values?.login_bg_image instanceof
                                  File
                                )
                              ) {
                                settingsForm.setFieldError(
                                  "login_bg_image",
                                  "Only JPG or PNG files are allowed."
                                );
                              }
                            }
                          }}
                          id="app_login_bg_image "
                          name="login_bg_image "
                        />
                        {settingsForm.errors.login_bg_image && (
                          <div className="invalidError d-flex justify-content-start">
                            {settingsForm.errors.login_bg_image}
                          </div>
                        )}
                      </FormGroup>
                    </Col>
                    <Col md={10}>
                      <FormGroup>
                        <Label htmlFor="app_favicon ">Favicon:</Label>
                        {settingsForm.values?.favicon && (
                          <img
                            src={
                              typeof settingsForm.values?.favicon == "string"
                                ? settingsForm.values?.favicon
                                : URL.createObjectURL(
                                    settingsForm.values?.favicon
                                  )
                            }
                            alt="App favicon Preview"
                            className="productImagePreview"
                          />
                        )}
                        <Input
                          type="file"
                          required={
                            typeof settingsForm.values?.favicon !== "string"
                          }
                          onChange={(event) => {
                            const file = event.currentTarget.files[0];
                            if (
                              file &&
                              (file.type === "image/jpeg" ||
                                file.type === "image/png" ||
                                file.type === "image/svg+xml")
                            ) {
                              settingsForm.setFieldValue("favicon", file);
                            } else {
                              // If invalid, clear the input and optionally show an error message
                              event.target.value = null;
                              if (
                                !(settingsForm.values?.favicon instanceof File)
                              ) {
                                settingsForm.setFieldError(
                                  "favicon",
                                  "Only JPG, PNG, or SVG files are allowed."
                                );
                              }
                            }
                          }}
                          id="app_favicon"
                          name="favicon "
                        />
                        {settingsForm.errors.favicon && (
                          <div className="invalidError d-flex justify-content-start">
                            {settingsForm.errors.favicon}
                          </div>
                        )}
                      </FormGroup>
                    </Col>

                    <Row>
                      <Col md={6}>
                        <FormGroup>
                          <Label htmlFor="app_arabic_tag_line">
                            Arabic Name:
                          </Label>
                          <Input
                            type="text"
                            value={settingsForm.values.arabic_tag_line}
                            onChange={settingsForm.handleChange}
                            id="app_arabic_tag_line"
                            name="arabic_tag_line"
                          />
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label htmlFor="app_tag_line">Tag Line:</Label>
                          <Input
                            type="text"
                            value={settingsForm.values.tag_line}
                            onChange={settingsForm.handleChange}
                            id="app_tag_line"
                            name="tag_line"
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                    <Col md={12}>
                      <FormGroup>
                        <Label htmlFor="app_address">Company Address:</Label>
                        <textarea
                          value={settingsForm.values.address}
                          className="selectInput"
                          required
                          onChange={settingsForm.handleChange}
                          id="app_address "
                          name="address"
                        />
                      </FormGroup>
                    </Col>
                    <Row>
                    <Col md={4}>
                      <FormGroup>
                        <Label htmlFor="country">Country:</Label>
                        <Select
                          options={memoizedCountryData}
                          placeholder="Select a country..."
                          isSearchable
                          value={selectedCountry}
                          onChange={handleCountrySelectChange}
                        />
                        {settingsForm.errors.country &&
                        settingsForm.touched.country ? (
                          <div className=" invalidError d-flex justify-content-center">
                            {settingsForm.errors.country}
                          </div>
                        ) : null}
                      </FormGroup>
                    </Col>
                    
                      {/* TAX */}
                      <Col md={3}>
                        <FormGroup>
                          <Label htmlFor="app_tax">Tax:</Label>
                          <select
                              className="selectInput"
                              id="app_tax"
                              name="tax"
                              required
                              value={settingsForm.values.tax}
                              onChange={settingsForm.handleChange}
                            >
                              <option value={""}>Select tax</option>
                              {taxList?.map((item) => (
                                <option key={item.id} value={item.id}>
                                  {item.name}
                                </option>
                              ))}
                            </select>
                        </FormGroup>
                      </Col>
                        {/* Type */}
                        <Col md={2}>
                        <FormGroup>
                          <Label htmlFor="app_tax_type">Type:</Label>
                          <select
                              className="selectInput"
                              id="app_tax_type"
                              name="tax_type"
                              required
                              value={settingsForm.values.tax_type}
                              onChange={settingsForm.handleChange}
                            >
                            <option value={""}>Select type</option>
                            {taxTypes.map((item) => (
                                <option key={item.id} value={item.value}>
                                    {item.name}
                            </option>
                            ))}
                            </select>
                        </FormGroup>
                      </Col>
                        {/* Value */}
                        <Col md={3}>
                        <FormGroup>
                          <Label htmlFor="tax_value">Value:</Label>
                          <Input
                            type="text"
                            value={settingsForm.values.tax_value}
                            required
                            onChange={settingsForm.handleChange}
                            id="taxation_value"
                            name="tax_value"
                          />
                        </FormGroup>
                      </Col>

                      {/* TAX NO */}
                      {settingsForm.values.tax &&
                      <Col md={4}>
                          <FormGroup>
                            <Label htmlFor="app_tax_no">VAT No:</Label>
                            <Input
                              type="text"
                              value={settingsForm.values.tax_no}
                              required
                              onChange={settingsForm.handleChange}
                              id="app_tax_no"
                              name="tax_no"
                            />
                          </FormGroup>
                      </Col>}
                      {/* License no */}
                      <Col md={4}>
                        <FormGroup>
                          <Label htmlFor="license_no">License No:</Label>
                          <Input
                            type="text"
                            value={settingsForm.values.license_number}
                            required
                            onChange={settingsForm.handleChange}
                            id="license_no"
                            name="license_number"
                          />
                        </FormGroup>
                      </Col>
                      {/* Markup */}
                      <Col md={4}>
                        <FormGroup>
                          <Label htmlFor="markup">Standard Markup (%):</Label>
                          <Input
                            type="text"
                            value={settingsForm.values.standard_markup}
                            required
                            onChange={settingsForm.handleChange}
                            id="markup"
                            name="standard_markup"
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={12}><Label className="h3 " htmlFor="quot_prefix"><b>Quotation Details:</b></Label></Col>
                      {/* Quot NO */}
                      <Col md={6}>
                        <FormGroup>
                          <Label htmlFor="quot_prefix">Quot Prefix:</Label>
                          <Input
                            type="text"
                            value={settingsForm.values.quote_prefix}
                            required
                            onChange={settingsForm.handleChange}
                            id="quot_prefix"
                            name="quote_prefix"
                          />
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label htmlFor="validity_period">Quotation Validity:</Label>
                          <Input
                            type="text"
                            value={settingsForm.values.quote_validity_period}
                            required
                            onChange={settingsForm.handleChange}
                            placeholder="e.g., 30 Days From The Date of Quotation"
                            id="validity_period"
                            name="quote_validity_period"
                          />
                        </FormGroup>
                      </Col>
                      {/* <Col md={6}>
                        <FormGroup>
                          <Label htmlFor="quot_delivery_terms">Delivery Time:</Label>
                          <textarea
                            value={settingsForm.values.quote_delivery_terms}
                            className="selectInput"
                            placeholder="e.g., 6-12 Weeks After PO and Advance Payment"
                            required
                            onChange={settingsForm.handleChange}
                            id="quot_delivery_terms"
                            name="quote_delivery_terms"
                        />
                        </FormGroup>
                      </Col> */}
      
                      <Col md={6}>
                        <FormGroup>
                          <Label htmlFor="payment_structure">Payment Structure:</Label>
                          <textarea
                            value={settingsForm.values.quote_payment_structure}
                            className="selectInput"
                            required
                            placeholder="e.g., 60% Advance, 30% Upon Material Delivery, 10% After Completion"
                            onChange={settingsForm.handleChange}
                            id="payment_structure "
                            name="quote_payment_structure"
                        />
                        </FormGroup>
                      </Col>
      
                    </Row>
                    <Col md={12} className="d-flex justify-content-end">
                      <button type="submit" className="btn btn-primary">
                        Submit
                      </button>
                    </Col>
                  </Form>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </>
  );
}

export default Settings;
